/**
 * Group Members page — lists all members of a group with expandable
 * item drill-downs, plus active invite links with revoke controls.
 *
 * Accessible via the Members stat in GroupView's stats strip.
 * Route: /groups/:groupId/members
 */
import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGroup, useGroupMembers } from '@/hooks/useGroups';
import { useGroupItems, type ItemWithOwner } from '@/hooks/useItems';
import { useGroupInvites, useRevokeInvite } from '@/hooks/useGroupInvites';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, ChevronDown, ChevronRight, Link2, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { T, RADIUS, SHADOW, SPACE } from '@/lib/tokens';
import { Layout } from '@/components/design/Layout';
import { Avatar } from '@/components/design/Avatar';
import { ItemListRow } from '@/components/design/ItemListRow';
import { InvitePanel } from '@/components/InvitePanel';

/**
 *
 */
export default function GroupMembers() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: group, isLoading: groupLoading } = useGroup(groupId!);
  const { data: members, isLoading: membersLoading } = useGroupMembers(groupId!);
  const { data: itemsData } = useGroupItems(groupId!);
  const { data: invites } = useGroupInvites(groupId!);
  const revokeInvite = useRevokeInvite();

  const items = useMemo(() => itemsData?.pages.flatMap((p) => p.items) ?? [], [itemsData]);

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [invitePanelOpen, setInvitePanelOpen] = useState(false);

  const toggleMember = (userId: string) => {
    setExpandedMemberId((prev) => (prev === userId ? null : userId));
  };

  /**
   * Items owned by a member within this group.
   * @param userId
   */
  const getOwnedItems = (userId: string): ItemWithOwner[] =>
    items.filter((i) => i.owner_id === userId);

  /**
   * Items currently being borrowed by a member within this group.
   * @param userId
   */
  const getBorrowedItems = (userId: string): ItemWithOwner[] =>
    items.filter((i) => i.active_loan?.borrower_id === userId);

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await revokeInvite.mutateAsync({ inviteId, groupId: groupId! });
      toast.success('Invite revoked');
    } catch {
      // Error toast handled by useRevokeInvite onError
    }
  };

  const isLoading = groupLoading || membersLoading;

  // Resolve invite creator names from member list
  const memberNameMap = useMemo(() => {
    const map = new Map<string, string>();
    members?.forEach((m) => map.set(m.user_id, m.display_name || 'Unknown'));
    return map;
  }, [members]);

  const leftAction = (
    <button
      onClick={() => navigate(`/groups/${groupId}`)}
      aria-label="Back to group"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        color: T.text,
      }}
    >
      <ArrowLeft size={22} />
    </button>
  );

  if (isLoading) {
    return (
      <Layout title="Members" leftAction={leftAction}>
        <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, fontSize: 14 }}>
          Loading…
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={group?.name ?? 'Members'} leftAction={leftAction}>
      {/* ─── Members Section ────────────────────────────────────────────── */}
      <div style={{ marginBottom: SPACE.sectionGap }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: T.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 10,
          }}
        >
          Members · {members?.length ?? 0}
        </div>

        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: RADIUS.lg,
            boxShadow: SHADOW.md,
            overflow: 'hidden',
          }}
        >
          {members?.map((member, idx) => {
            const isExpanded = expandedMemberId === member.user_id;
            const isCurrentUser = member.user_id === user?.id;
            const isCreator = member.user_id === group?.created_by;
            const ownedItems = getOwnedItems(member.user_id);
            const borrowedItems = getBorrowedItems(member.user_id);
            const isLast = idx === (members?.length ?? 0) - 1;

            return (
              <div key={member.id}>
                {/* Member row */}
                <button
                  type="button"
                  onClick={() => toggleMember(member.user_id)}
                  style={{
                    appearance: 'none',
                    font: 'inherit',
                    textAlign: 'left',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    cursor: 'pointer',
                    background: isExpanded ? T.surfaceSubtle : 'transparent',
                    transition: 'background 0.12s ease',
                    border: 'none',
                    borderBottom: isLast && !isExpanded ? 'none' : `1px solid ${T.border}`,
                  }}
                >
                  <Avatar
                    name={member.display_name || '?'}
                    size={38}
                    isCurrentUser={isCurrentUser}
                  />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: T.text,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {member.display_name || 'Unknown'}
                        {isCurrentUser && (
                          <span style={{ fontWeight: 400, color: T.textMuted, marginLeft: 4 }}>
                            (you)
                          </span>
                        )}
                      </span>
                      {isCreator && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: T.accent,
                            background: T.accentSoft,
                            padding: '1px 6px',
                            borderRadius: 4,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            flexShrink: 0,
                          }}
                        >
                          Creator
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                      Joined {formatDistanceToNow(new Date(member.created_at), { addSuffix: true })}
                      {' · '}
                      {ownedItems.length} item{ownedItems.length !== 1 ? 's' : ''}
                    </div>
                  </div>

                  <div style={{ flexShrink: 0, color: T.textMuted }}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                </button>

                {/* Expanded item drill-down */}
                {isExpanded && (
                  <div
                    style={{
                      background: T.surfaceSubtle,
                      borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
                      padding: '4px 8px 8px',
                    }}
                  >
                    {/* Owned items */}
                    {ownedItems.length > 0 && (
                      <div style={{ marginBottom: borrowedItems.length > 0 ? 12 : 0 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: T.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            padding: '8px 8px 4px',
                          }}
                        >
                          Owns {ownedItems.length} item{ownedItems.length !== 1 ? 's' : ''}
                        </div>
                        {ownedItems.map((item, i) => (
                          <ItemListRow
                            key={item.id}
                            item={item}
                            currentUserId={user!.id}
                            onClick={() => navigate(`/groups/${groupId}?item=${item.id}`)}
                            isLast={i === ownedItems.length - 1}
                          />
                        ))}
                      </div>
                    )}

                    {/* Borrowed items */}
                    {borrowedItems.length > 0 && (
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: T.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            padding: '8px 8px 4px',
                          }}
                        >
                          Borrowing {borrowedItems.length} item
                          {borrowedItems.length !== 1 ? 's' : ''}
                        </div>
                        {borrowedItems.map((item, i) => (
                          <ItemListRow
                            key={item.id}
                            item={item}
                            currentUserId={user!.id}
                            onClick={() => navigate(`/groups/${groupId}?item=${item.id}`)}
                            isLast={i === borrowedItems.length - 1}
                          />
                        ))}
                      </div>
                    )}

                    {/* Empty state */}
                    {ownedItems.length === 0 && borrowedItems.length === 0 && (
                      <div
                        style={{
                          fontSize: 13,
                          color: T.textMuted,
                          padding: '12px 8px',
                          textAlign: 'center',
                        }}
                      >
                        No items in this group yet
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Active Invites Section ─────────────────────────────────────── */}
      {((invites && invites.length > 0) || group?.created_by === user?.id) && (
        <div style={{ marginBottom: SPACE.sectionGap }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: T.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Active Invites · {invites?.length ?? 0}
            </div>
            <button
              onClick={() => setInvitePanelOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 13,
                fontWeight: 600,
                color: T.accent,
                padding: '2px 0',
              }}
            >
              <Link2 size={14} />
              New Invite
            </button>
          </div>

          {invites && invites.length > 0 ? (
            <div
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.lg,
                boxShadow: SHADOW.md,
                overflow: 'hidden',
              }}
            >
              {invites.map((invite, idx) => (
                <div
                  key={invite.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 16px',
                    borderBottom: idx < invites.length - 1 ? `1px solid ${T.border}` : 'none',
                  }}
                >
                  <Link2 size={16} style={{ color: T.textMuted, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: T.text,
                        fontFamily: 'monospace',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {invite.invite_code}
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>
                      {memberNameMap.get(invite.created_by) ?? 'Unknown'}
                      {' · Expires '}
                      {formatDistanceToNow(new Date(invite.expires_at), { addSuffix: true })}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeInvite(invite.id)}
                    disabled={revokeInvite.isPending}
                    aria-label="Revoke invite"
                    style={{
                      background: 'none',
                      border: `1px solid ${T.border}`,
                      borderRadius: RADIUS.sm,
                      cursor: 'pointer',
                      padding: '4px 6px',
                      display: 'flex',
                      alignItems: 'center',
                      color: T.textMuted,
                      flexShrink: 0,
                      transition: 'color 0.12s, border-color 0.12s',
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                fontSize: 13,
                color: T.textMuted,
                padding: '8px 0',
              }}
            >
              No active invites
            </div>
          )}
        </div>
      )}

      {/* Reuse existing InvitePanel dialog */}
      <InvitePanel open={invitePanelOpen} onOpenChange={setInvitePanelOpen} groupId={groupId!} />
    </Layout>
  );
}
